import { setDefaultOptions, loadModules } from 'esri-loader';
import _uniqueId from 'lodash/uniqueId';

import store from '../store/store';
import config from '../config/config';
import MapController from './MapController';

import {
  setSketchToolActive,
  setDrawComplete,
  setAnnotationToolActive,
  setContextualSketchToolActive,
  setRiverSketchToolActive,
  storeTempGraphic,
  toggleLayerModal,
  toggleMapLoader,
} from '../reducers/mapview/actions';
import { setAuditArea, setContextualArea } from '../reducers/auditdata/actions';
import { toggleToastError } from '../reducers/mapview/actions';
import { createAnnotationPopup } from '../utils/createAnnotationPopup.utils';
import mapController from './MapController';
import { configurePopupObserver } from '../utils/miscHelperFunctions.utils';
import ga, { GAActions, GACategories } from '../utils/googleAnalytics.utils';

setDefaultOptions({ css: true, version: config.esri.version });

/**
 * Class used to control the behaviour of the Sketch widget
 */
export class SketchController {
  _sketch: __esri.Sketch | undefined;
  _contextualSketch: __esri.Sketch | undefined;
  _annotationSketch: __esri.Sketch | undefined;
  _riverSketch: __esri.Sketch | undefined;
  _graphicsLayer: __esri.GraphicsLayer | undefined;
  _dataType: string;
  _popupObserver: MutationObserver | null = null;

  constructor() {
    this._sketch = undefined;
    this._contextualSketch = undefined;
    this._riverSketch = undefined;
    this._annotationSketch = undefined;
    this._dataType = 'audit';
  }

  /**
   * Initialize the Sketch widget
   * @param mapview - The app's mapview component
   * @param graphicsLayer - The graphic layer we add Sketch graphics to
   */
  initializeSketchTool = async (mapview: __esri.MapView | undefined) => {
    const [Sketch, SketchViewModel, GraphicsLayer] = await loadModules(['esri/widgets/Sketch', 'esri/widgets/Sketch/SketchViewModel', 'esri/layers/GraphicsLayer']);

    if (mapview) {
      this._graphicsLayer = new GraphicsLayer({
        id: 'auditAreaSketchLayer',
      });
      mapview.map.add(this._graphicsLayer!);
      this._sketch = new Sketch({
        creationMode: 'single',
        defaultUpdateOptions: {
          enableRotation: false,
          enableScaling: false,
          multipleSelectionEnabled: false,
          toggleToolOnClick: false,
        },
        viewModel: new SketchViewModel({
          layer: this._graphicsLayer,
          view: mapview,
          polygonSymbol: {
            type: 'simple-fill',
            color: [251, 157, 15, 0.0],
            outline: {
              color: '#FB9D0F',
              width: 2,
            },
          },
        }),
      });
      this._sketch?.on('create', async (event) => {
        const [GeometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
        const auditLayer = mapview.map.findLayerById('auditAreaLayer') as __esri.GraphicsLayer;
        auditLayer.removeAll(); //remove the graphic that was just added
        auditLayer.add(event.graphic);
        switch (event.state) {
          case 'complete':
            const maxArea = config.audit.maxAuditArea;
            const actualArea = GeometryEngine.geodesicArea(event.graphic.geometry, 'square-kilometers');
            const hectares = GeometryEngine.geodesicArea(event.graphic.geometry, 'hectares')
            const hansenCounts =  await mapController.runHansenAnalysis(event.graphic.geometry);
            if (actualArea > maxArea) {
              store.dispatch(
                toggleToastError(true, 'The selected area is too large - please try again with a smaller area.')
              );
              auditLayer.removeAll(); //remove the graphic that was just added
              this._graphicsLayer?.removeAll(); // Clear graphics layer as we don't need the graphic anymore
              this._sketch?.create('polygon');
              store.dispatch(toggleMapLoader(false));
              return;
            }
            store.dispatch(this._dataType === 'audit' ? setAuditArea(event.graphic, hectares, hansenCounts) : setContextualArea(event.graphic));
            store.dispatch(setDrawComplete(true));
            store.dispatch(setSketchToolActive(false));
            MapController.zoomToAuditArea(event.graphic);
            ga.event(GACategories.Polygon, GAActions.Added, this._dataType === 'audit' ? "Audit" : "Contextual");
            MapController.resetAuditAreaLayerIfNeeded();
            this._graphicsLayer?.removeAll(); // Clear graphics layer as we don't need the graphic anymore
            break;
          case 'cancel':
            store.dispatch(setSketchToolActive(false));
            auditLayer.removeAll(); //remove the graphic that was just added
            break;
        }
      });
      //We want to ensure that a user can't update the polygon after they've created it. Esri doesn't offer that functionality so we have to cancel out of the update operation whenver the update event is fired
      this._sketch?.viewModel.on('update', () => {
        this._sketch?.viewModel.cancel();
      });
    }
  };

  /**
   * Toggle the Sketch tool on and off
   * @param toggleSketchTool - a boolean dictating whether to toggle the Sketch tool on or off
   */
  toggleAuditSketchTool = async (toggleSketchTool: boolean, dataType: string) => {
    this._dataType = dataType;
    store.dispatch(setSketchToolActive(toggleSketchTool));
    if (store.getState().mapview.drawComplete) {
      store.dispatch(setDrawComplete(false));
    }
    this._graphicsLayer?.removeAll();
    // const auditLayer = mapview.map.findLayerById('auditAreaLayer');
    // auditLayer.removeAll();
    if (toggleSketchTool) {
      this.disableSketchTools();
      this.disableMeasurementTools();
      this._sketch?.create('polygon', {
        mode: 'hybrid',
      });
    } else {
      this._sketch?.cancel();
    }
  };

  /**
   * method to initialize the contextual sketch tool
   * @param mapview - mapview where the contextual sketch widget will reside
   * @param graphicsLayer - layer that will be connected to the contextual sketch widget
   */
  initializeContextualSketchTool = async (mapview: __esri.MapView | undefined) => {
    const [Sketch, SketchViewModel, geometryEngine, GraphicsLayer] = await loadModules([
      'esri/widgets/Sketch',
      'esri/widgets/Sketch/SketchViewModel',
      'esri/geometry/geometryEngine',
      'esri/layers/GraphicsLayer'
    ]);

    if (mapview) {
      const layer = new GraphicsLayer({
        id: 'contextualSketchLayer',
      });
      mapview.map.add(layer!);

      this._contextualSketch = new Sketch({
        creationMode: 'single',
        viewModel: new SketchViewModel({
          layer: layer,
          view: mapview,
          polygonSymbol: {
            type: 'simple-fill',
            color: [1, 103, 167, 0.39],
            outline: {
              color: '#0022D9',
              width: 2,
            },
          },
        }),
      });

      this._contextualSketch?.on('create', (event) => {
        const contextualLayer = mapview.map.findLayerById('contextualLayer') as __esri.GraphicsLayer;
        contextualLayer.add(event.graphic);

        if (event.state === 'complete') {
          const id = `${_uniqueId('my-layers')}-contextual`;
          const area = geometryEngine.geodesicArea(event.graphic.geometry, 'hectares');
          event.graphic.attributes = {
            id,
            area,
          };

          store.dispatch(storeTempGraphic(event.graphic));
          store.dispatch(toggleLayerModal(true));

          store.dispatch(setContextualSketchToolActive(false));

          ga.event(GACategories.Polygon, GAActions.Added, "Contextual");
          layer.removeAll();
        } else if (event.state === 'cancel') {
          store.dispatch(setContextualSketchToolActive(false));
          contextualLayer.removeAll(); //remove the graphic that was just added
        }
      });
    }
  };

  /**
   * Toggle the Sketch tool on and off
   * @param toggleSketchTool - a boolean dictating whether to toggle the Sketch tool on or off
   */
  toggleContextualSketchTool = (toggleSketchTool: boolean) => {
    store.dispatch(setContextualSketchToolActive(toggleSketchTool));

    if (toggleSketchTool) {
      this.disableSketchTools();
      this.disableMeasurementTools();
      this._contextualSketch?.create('polygon', {
        mode: 'hybrid',
      });
    } else {
      this._contextualSketch?.cancel();
    }
  };

  /**
   * method to initialize the river boundaries sketch tool
   * @param mapview - mapview where the river sketch widget will reside
   * @param graphicsLayer - layer that will be connected to the river sketch widget
   */
  initializeRiverSketchTool = async (mapview: __esri.MapView | undefined) => {
    const [Sketch, SketchViewModel, GraphicsLayer] = await loadModules(['esri/widgets/Sketch', 'esri/widgets/Sketch/SketchViewModel', 'esri/layers/GraphicsLayer']);

    if (mapview) {
      const layer = new GraphicsLayer({
        id: 'riverSketchLayer',
      });
      mapview.map.add(layer!);
      this._riverSketch = new Sketch({
        creationMode: 'single',
        viewModel: new SketchViewModel({
          layer: layer,
          view: mapview,
          polylineSymbol: {
            type: 'simple-line',
            color: [0, 85, 255, 0.79],
            width: 2,
          },
        }),
      });

      this._riverSketch?.on('create', (event) => {
        const riverLayer = mapview.map.findLayerById('riverLayer') as __esri.GraphicsLayer;
        riverLayer.add(event.graphic);
        if (event.state === 'complete') {
          // Add graphic to River Layer
          store.dispatch(setRiverSketchToolActive(false));
          ga.event(GACategories.Polygon, GAActions.Added, "River Boundary");
          layer.removeAll();
        } else if (event.state === 'cancel') {
          store.dispatch(setContextualSketchToolActive(false));
          riverLayer.removeAll(); //remove the graphic that was just added
        }
      });
    }
  };

  /**
   *  Disabling any active sketch tools if toggling another one ON. This makes sure that only one sketch tool is enabled
   *  at a time.
   */
  disableSketchTools = () => {
    if (this._sketch?.activeTool !== null) {
      store.dispatch(setSketchToolActive(false));
      this._sketch?.cancel();
    } else if (this._annotationSketch?.activeTool !== null) {
      store.dispatch(setAnnotationToolActive(false));
      this._annotationSketch?.cancel();
    } else if (this._riverSketch?.activeTool !== null) {
      store.dispatch(setRiverSketchToolActive(false));
      this._riverSketch?.cancel();
    } else if (this._contextualSketch?.activeTool !== null) {
      store.dispatch(setContextualSketchToolActive(false));
      this._contextualSketch?.cancel();
    }
  };

  /**
   * Disables any measurement tools that might be active
   */
  disableMeasurementTools = () => {
    MapController._areaMeasurementWidget?.viewModel.clear();
    MapController._distanceMeasurementWidget?.viewModel.clear();
    MapController.activeMeasurementWidget = null;
  };

  /**
   * Toggle the Sketch tool on and off
   * @param toggleSketchTool - a boolean dictating whether to toggle the Sketch tool on or off
   */
  toggleRiverSketchTool = (toggleSketchTool: boolean) => {
    store.dispatch(setRiverSketchToolActive(toggleSketchTool));

    if (toggleSketchTool) {
      this.disableSketchTools();
      this.disableMeasurementTools();
      this._riverSketch?.create('polyline', {
        mode: 'hybrid',
      });
    } else {
      this._riverSketch?.cancel();
    }
  };

  /**
   * toggle the Sketch tool on and off
   * @param toggleSketchTool - a boolean dictating whether to toggle the Annotations Sketch tool on or off
   */
  toggleAnnotationSketchTool = (toggleSketchTool: boolean) => {
    this.disableSketchTools();
    this.disableMeasurementTools();
    MapController._mapview?.popup.close();
    store.dispatch(setAnnotationToolActive(toggleSketchTool));
    this._annotationSketch?.create('polygon');
  };

  /**
   * method to initialize the annotations sketch tool
   * @param mapview - mapview where the annotation sketch widget will reside
   * @param graphicsLayer - layer that will be connected to the annotation sketch widget
   */
  initializeAnnotationSketchTool = async (mapview: __esri.MapView | undefined, graphicsLayer: __esri.GraphicsLayer) => {
    const [Sketch, SketchViewModel, { watch }] = await loadModules([
      'esri/widgets/Sketch',
      'esri/widgets/Sketch/SketchViewModel',
      'esri/core/watchUtils',
    ]);

    if (mapview) {
      this._annotationSketch = new Sketch({
        creationMode: 'single',
        viewModel: new SketchViewModel({
          layer: graphicsLayer,
          view: mapview,
          polygonSymbol: {
            type: 'simple-fill',
            color: [167, 67, 254, 0.39],
            outline: {
              color: '#5A01A7',
              width: 2,
            },
          },
        }),
      });

      this._annotationSketch?.on('create', (event) => {
        if (event.state === 'complete') {
          const id = Math.floor(Math.random() * 10000000);
          event.graphic.attributes = {
            title: 'Annotation',
            desc: '',
            id,
            editMode: true,
          };

          configurePopupObserver(true, this._popupObserver);

          mapview.popup.open({
            location: (event.graphic.geometry as __esri.Polygon).centroid,
            content: createAnnotationPopup({
              graphic: event.graphic,
              close: () => {
                this._annotationSketch?.complete();
                mapview.popup.close();
              },
            }),
          });

          this._annotationSketch?.update([event.graphic]);
          store.dispatch(setAnnotationToolActive(false));

          ga.event(GACategories.Polygon, GAActions.Added, "Annotation");
        }
      });

      this._annotationSketch?.on('update', (event) => {
        if (event.state === 'active') {
          mapview.popup.close();
        }
      });

      this._annotationSketch?.on('delete', (event) => {
        MapController.deleteAnnotation();
      });

      watch(this._annotationSketch, 'activeTool', (newValue: string, oldValue: string) => {
        if (newValue === 'reshape' && oldValue !== newValue) {
          mapview.popup.close();
        }
      });
    }
  };

  /**
   * method to set the annotations sketch tool into update mode for a specific graphic
   * @param graphic - annotation graphic that we want to update
   */
  editAnnotation = (graphic: __esri.Graphic) => {
    this._annotationSketch?.update([graphic]);
  };

  /**
   * method to add a clicked forest boundary as the audit area
   * @param boundary - forest boundary graphic that we want to use as FMU
   */
  addBoundaryToAuditAreaSketchLayer = async (boundary: __esri.Graphic) => {
    const [GeometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
    const area = GeometryEngine.geodesicArea(boundary.geometry, 'hectares');
    const hansenCounts =  await mapController.runHansenAnalysis(boundary.geometry);

    if (this._sketch) {
      boundary.symbol = this._sketch.viewModel.polygonSymbol; // no symbol on graphic, so we set to the sketch tool symbol
      store.dispatch(this._dataType === 'audit' ? setAuditArea(boundary, area, hansenCounts) : setContextualArea(boundary));
      store.dispatch(setDrawComplete(true));
      store.dispatch(setSketchToolActive(false));
      MapController.zoomToAuditArea(boundary);
    }
  };

  /**
   * method to clear sketch tool of area
   * @param dataType - either 'audit' or 'contextual'
   */
  clearArea = (dataType: string) => {
    store.dispatch(dataType === 'audit' ? setAuditArea(null, 0, []) : setContextualArea(null));
    MapController.deleteArea();
  };
}

const sketchController = new SketchController();

//TODO: This is for DEV only and should be removed once we deploy to prod
declare global {
  interface Window {
    sketchController: typeof sketchController;
  }
}

window.sketchController = sketchController;

export default sketchController;
